import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import FilterBar, { type AppliedFilter } from '@components/Common/FilterBar';
import SearchInput from '@components/Common/SearchInput';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import ToggleField from '@components/Common/ToggleField';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import {
  type BulkStatusResponse,
  type BulkSummary,
  EMPTY_GUIDE_LIST_STATE,
  GUIDE_PAGE_SIZE,
  type GuideListState,
  SORT_OPTIONS,
  STATUS_FILTERS,
  STATUS_LABELS,
  TYPE_FILTERS,
  TYPE_LABEL,
  parseGuideListParams,
  summariseBulkResult,
  toGuideApiParams,
  toGuideListParams,
  toggleStatus,
} from './guideListQuery';
import { formatCost, reportFromDone } from './GuideEditor/importReportData';
import { readImportStream } from './importStream';
import { useBeforeUnloadWhile } from './useBeforeUnloadWhile';
import { useDraftWarnings } from './reviewWarnings';
import DraftWarningsCell from './DraftWarningsCell';
import './index.scss';
import { formatDate } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
/* Selection rides in the title cell rather than a column of its own: MintlyTable renders the first
   column as a phone card's title, so a checkbox column first would title every card with a box,
   and a checkbox column anywhere else would sit apart from the thing it selects. */
const GUIDE_COLUMNS = ['title', 'pageType', 'status', 'datePublished', 'viewCount', 'actions'];

interface GuideRow {
  _id: string;
  slug: string;
  pageType: string;
  title: string;
  excerpt?: string;
  status?: string;
  datePublished?: string;
  viewCount?: number;
  sourceFileUrl?: string;
  updatedAt?: string;
}

interface GuideCounts {
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  total: number;
}

/** How long to wait after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 350;

/* The colour is CRMStatusChip's; only the word is ours. A published guide is the same ACTIVE
   green as an active rule or an active company, it is just called something else here. */
/*
 * IMPORTING and IMPORT_FAILED are real statuses the extract route writes, and both used to fall
 * through to "Draft" — so a row whose import died looked exactly like a guide someone had started
 * by hand, and the only clue was the title reading "Import failed: foo.docx".
 */
const statusChip = (status?: string) => {
  const s = (status || 'DRAFT').toUpperCase();
  return <CRMStatusChip status={s} label={STATUS_LABELS[s] ?? s} />;
};

/*
 * A filter chip's look follows whether it is pressed, never which status it names: the status
 * colours belong to CRMStatusChip in the table. Same filled-brand / outlined pair the SearchFilter
 * option chips use. The fill is in index.scss rather than `color="primary"`, because the theme's
 * MuiChip root override pins the label colour and the background, so a primary chip rendered
 * exactly like an unpressed one.
 */
const pressedChip = (on: boolean) =>
  ({
    variant: on ? 'filled' : 'outlined',
    className: `guides-list__status-chip${on ? ' is-pressed' : ''}`,
  }) as const;

const isImportRow = (row: GuideRow) => {
  const s = (row.status || '').toUpperCase();
  return s === 'IMPORTING' || s === 'IMPORT_FAILED';
};

/**
 * The guides list: one page at a time, filtered and searched on the server.
 *
 * It fetched every guide (up to a silent cap of 500) and drew them all, which is fine at sixteen
 * and is a list that stops at 500 without saying so at the scale the AI import was built for. The
 * BFF now pages, filters, searches, sorts and counts; this owns the controls and the URL.
 *
 * Two filter surfaces, deliberately, and they are not the two CLAUDE.md warns about. The status
 * chips are the page's partition, with counts, in the role the pipeline tiles play on the résumé
 * import page: they answer "what is stuck?" first and filter second. Everything else is a query
 * and lives in the one `FilterBar`. Search applies itself after a pause rather than waiting for
 * Apply, because Apply-to-search reads as a box that does nothing; Apply still commits it at once.
 */
const Guides: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseGuideListParams(searchParams), [searchParams]);

  const [rows, setRows] = useState<GuideRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<GuideCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [pendingDelete, setPendingDelete] = useState<GuideRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** The row whose status is being written. A switch that stays clickable sends the write twice. */
  const [settingStatus, setSettingStatus] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<'ACTIVE' | 'DRAFT' | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<BulkSummary | null>(null);

  /** Rows with a retry streaming, and how long each has been running. */
  const [retrying, setRetrying] = useState<Record<string, number>>({});
  useBeforeUnloadWhile(Object.keys(retrying).length > 0);

  const setQuery = useCallback(
    (next: GuideListState) => setSearchParams(toGuideListParams(next), { replace: false }),
    [setSearchParams],
  );

  /* ── Search: typed into a draft, applied after a pause ───────────────── */

  const [qDraft, setQDraft] = useState(query.q);
  // Back/forward or a chip click can change the URL's q underneath the box; follow it.
  useEffect(() => setQDraft(query.q), [query.q]);
  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    if (qDraft.trim() === queryRef.current.q.trim()) return undefined;
    const t = setTimeout(
      () => setQuery({ ...queryRef.current, q: qDraft.trim(), page: 0 }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [qDraft, setQuery]);

  /* ── Data ─────────────────────────────────────────────────────────────── */

  const apiParams = useMemo(() => toGuideApiParams(query), [query]);
  const apiKey = JSON.stringify(apiParams);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    axios
      .get('/api/guides', { params: JSON.parse(apiKey) })
      .then(({ data }) => {
        if (cancelled) return;
        setRows(Array.isArray(data?.content) ? data.content : []);
        setTotal(Number(data?.totalElements ?? 0));
      })
      .catch((e) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setLoadError(e?.response?.data?.error || 'Failed to load guides');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      // An older, slower page must not land on top of the one the operator just asked for.
      cancelled = true;
    };
  }, [apiKey, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/guides/counts', { params: query.pageType ? { pageType: query.pageType } : {} })
      .then(({ data }) => {
        if (!cancelled && data?.byStatus) setCounts(data);
      })
      // The chips still filter without numbers on them; a missing count is not worth a toast.
      .catch(() => !cancelled && setCounts(null));
    return () => {
      cancelled = true;
    };
  }, [query.pageType, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // A selection belongs to the rows on screen. Carrying it to another page or filter would let
  // Publish act on guides the operator can no longer see.
  useEffect(() => setSelected(new Set()), [apiKey]);

  /* ── Row actions ──────────────────────────────────────────────────────── */

  const onDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/guides/${pendingDelete._id}`);
      showToast('Guide deleted', 'success');
      setPendingDelete(null);
      reload();
    } catch {
      showToast('Failed to delete guide', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const runBulk = async (ids: string[], status: 'ACTIVE' | 'DRAFT'): Promise<BulkStatusResponse> => {
    const { data } = await axios.post('/api/guides/bulk-status', { ids, status });
    return data as BulkStatusResponse;
  };

  /**
   * The row switch, through the same endpoint and the same server-side gate as bulk.
   *
   * It used to GET the whole guide and PUT it back, which ran the gate too but only if the PUT
   * carried exactly the fields the gate keys on: it once omitted contentHtml, and an HTML guide
   * was judged by the template rule and refused for sections it does not have. The bulk route
   * reads the stored guide itself, so there is nothing for this side to forget.
   */
  const onSetStatus = async (row: GuideRow, status: 'ACTIVE' | 'DRAFT') => {
    setSettingStatus(row._id);
    try {
      const result = await runBulk([row._id], status);
      const mine = result.results?.[0];
      if (mine && !mine.ok) {
        showToast(`Cannot publish: ${mine.problems?.[0] ?? 'the guide failed the publish check.'}`, 'error');
      } else {
        showToast(status === 'ACTIVE' ? 'Guide published' : 'Guide unpublished', 'success');
      }
      reload();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to update status', 'error');
    } finally {
      setSettingStatus(null);
    }
  };

  const onBulk = async () => {
    if (!bulkConfirm) return;
    const status = bulkConfirm;
    setBulkBusy(true);
    try {
      const result = await runBulk([...selected], status);
      const summary = summariseBulkResult(result);
      setBulkSummary(summary);
      showToast(summary.headline, summary.severity);
      setSelected(new Set());
      reload();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Could not change the selected guides', 'error');
    } finally {
      setBulkBusy(false);
      setBulkConfirm(null);
    }
  };

  /**
   * Re-run a failed or stalled import from its archived source.
   *
   * The work happens on the server and is saved onto the row whether or not this page is still
   * open, so leaving is safe; the stream is read here only to report the outcome.
   */
  const onRetry = async (row: GuideRow) => {
    const startedAt = Date.now();
    setRetrying((r) => ({ ...r, [row._id]: 0 }));
    try {
      const res = await fetch(`/api/guides/${row._id}/retry-import`, { method: 'POST' });
      showToast('Import restarted. It takes four to six minutes; this row updates when it finishes.', 'info');
      reload();
      const done = await readImportStream(res, (evt) => {
        if (evt.stage === 'working') {
          setRetrying((r) => ({ ...r, [row._id]: Math.round((Date.now() - startedAt) / 1000) }));
        }
      });
      if (done.saveError) {
        showToast(`Generated, but not saved: ${done.saveError}`, 'error');
      } else if (done.slugRenamed) {
        showToast(
          `Imported as a draft at /${done.slugRenamed.to}: /${done.slugRenamed.from} already belongs to another guide.`,
          'warning',
        );
      } else {
        // The cost of the run, so the retry is not the one generation whose price nobody sees.
        const report = reportFromDone(done);
        const cost = report?.usage ? ` (${formatCost(report.usage.estimatedCostUsd)})` : '';
        const fixes = report?.problems.length ? ` It has ${report.problems.length} thing(s) to fix before publishing.` : '';
        showToast(`Import finished${cost}. The guide is a draft; review it before publishing.${fixes}`, fixes ? 'warning' : 'success');
      }
    } catch (e: any) {
      showToast(e?.message || 'The retry failed', 'error');
    } finally {
      setRetrying((r) => {
        const { [row._id]: _gone, ...rest } = r;
        return rest;
      });
      reload();
    }
  };

  /**
   * State and the control that changes it, in one place.
   *
   * IMPORTING and IMPORT_FAILED keep the chip and get no switch. Publishing those would publish
   * the import stub: a real guide URL serving the 81 characters /extract writes before
   * generation starts.
   */
  const publishToggle = (row: GuideRow) => {
    const status = (row.status || 'DRAFT').toUpperCase();
    if (isImportRow(row)) return statusChip(status);
    const published = status === 'ACTIVE';
    return (
      // The row itself opens the editor, so the switch has to keep its click to itself.
      <Box onClick={(e) => e.stopPropagation()}>
        <ToggleField
          size="small"
          label={published ? 'Published' : 'Draft'}
          checked={published}
          disabled={settingStatus === row._id}
          onChange={(next) => onSetStatus(row, next ? 'ACTIVE' : 'DRAFT')}
        />
      </Box>
    );
  };

  const onPreview = async (row: GuideRow) => {
    try {
      const { data } = await axios.get(`/api/guides/${row._id}/preview-url`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      // A 503 here names the missing MAIN_SITE_URL or GUIDE_PREVIEW_SECRET, which is the whole
      // diagnosis.
      showToast(e?.response?.data?.error || 'Failed to open preview', 'error');
    }
  };

  const onDownloadSource = async (row: GuideRow) => {
    try {
      const { data } = await axios.get(`/api/guides/${row._id}/source`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      showToast('No source file available', 'error');
    }
  };

  /* ── Selection ────────────────────────────────────────────────────────── */

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r._id));
  const someOnPage = rows.some((r) => selected.has(r._id));
  const toggleAll = () =>
    setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r._id)));

  /* ── Filters ──────────────────────────────────────────────────────────── */

  const applied: AppliedFilter[] = [
    ...(query.q ? [{ key: 'q', label: `Search: ${query.q}`, onClear: () => setQuery({ ...query, q: '', page: 0 }) }] : []),
    ...(query.pageType
      ? [{ key: 'pageType', label: `Type: ${TYPE_LABEL[query.pageType]}`, onClear: () => setQuery({ ...query, pageType: '', page: 0 }) }]
      : []),
    ...(query.sort
      ? [{
          key: 'sort',
          label: `Sort: ${SORT_OPTIONS.find((s) => s.value === query.sort)?.label}`,
          onClear: () => setQuery({ ...query, sort: '', page: 0 }),
        }]
      : []),
  ];

  const typeOptions = [
    { value: '', label: 'All types' },
    ...TYPE_FILTERS.map((t) => ({
      value: t.value,
      label: counts ? `${t.label} (${counts.byType[t.value] ?? 0})` : t.label,
    })),
  ];

  const statusChips = STATUS_FILTERS.filter(
    // "As applicable": a status with no guides is noise, unless it is the one selected.
    (s) => !counts || (counts.byStatus[s.value] ?? 0) > 0 || query.statuses.includes(s.value),
  );

  const selectedCount = selected.size;

  /* ── Review queue (Phase 2d) ──────────────────────────────────────────────
     A preset over the existing status filter: `review=1` narrows to drafts and adds a warnings
     column read lazily per visible draft. The bulk-publish machinery above is unchanged; this only
     surfaces, inline, what a reviewer would otherwise open each draft to find. */
  const reviewMode = searchParams.get('review') === '1';
  const draftWarnings = useDraftWarnings(rows, reviewMode);
  const columns = reviewMode
    ? ['title', 'pageType', 'status', 'warnings', 'datePublished', 'viewCount', 'actions']
    : GUIDE_COLUMNS;
  const enterReview = () => {
    const params = toGuideListParams({ ...EMPTY_GUIDE_LIST_STATE, statuses: ['DRAFT'] });
    params.set('review', '1');
    setSearchParams(params);
  };

  return (
    <CRMPageTemplate
      icon={<MenuBookOutlinedIcon />}
      title="Guides"
      totalCount={total}
      onAddNew={() => navigate('/guides/new')}
      addNewLabel="New Guide"
    >
      <Box className="guides-list">
        <Box className="guides-list__toolbar">
          <Button
            size="small"
            variant="outlined"
            startIcon={<DriveFolderUploadOutlinedIcon />}
            onClick={() => navigate('/guides/import')}
          >
            Bulk import
          </Button>
          <Button
            size="small"
            variant={reviewMode ? 'contained' : 'outlined'}
            startIcon={<FactCheckOutlinedIcon />}
            onClick={reviewMode ? () => setQuery({ ...EMPTY_GUIDE_LIST_STATE }) : enterReview}
          >
            {reviewMode ? 'Exit review' : 'Review drafts'}
          </Button>
        </Box>

        <Box className="guides-list__statuses" role="group" aria-label="Filter by status">
          <Chip
            clickable
            aria-pressed={query.statuses.length === 0}
            {...pressedChip(query.statuses.length === 0)}
            label={counts ? `All ${counts.total}` : 'All'}
            onClick={() => setQuery({ ...query, statuses: [], page: 0 })}
          />
          {statusChips.map((s) => {
            const on = query.statuses.includes(s.value);
            return (
              <Chip
                key={s.value}
                data-status={s.value}
                clickable
                aria-pressed={on}
                {...pressedChip(on)}
                label={counts ? `${s.label} ${counts.byStatus[s.value] ?? 0}` : s.label}
                onClick={() => setQuery(toggleStatus(query, s.value))}
              />
            );
          })}
        </Box>

        <FilterBar
          applied={applied}
          onApply={() => setQuery({ ...query, q: qDraft.trim(), page: 0 })}
          onClearAll={() => {
            setQDraft('');
            setQuery({ ...query, q: '', pageType: '', sort: '', page: 0 });
          }}
        >
          <Box className="cc-filterbar__search">
            <SearchInput
              value={qDraft}
              onChange={setQDraft}
              placeholder="Search title or slug"
              onClear={() => setQDraft('')}
            />
          </Box>
          <SelectFormField
            required={false}
            name="guides-type"
            label="Type"
            options={typeOptions}
            value={query.pageType}
            onChange={(_n, v) => setQuery({ ...query, pageType: String(v), page: 0 })}
          />
          <SelectFormField
            required={false}
            name="guides-sort"
            label="Sort"
            options={SORT_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
            value={query.sort}
            onChange={(_n, v) => setQuery({ ...query, sort: String(v), page: 0 })}
          />
        </FilterBar>

        {bulkSummary && (
          <Alert
            className="guides-list__bulk-result"
            severity={bulkSummary.severity}
            onClose={() => setBulkSummary(null)}
          >
            <AlertTitle>{bulkSummary.headline}</AlertTitle>
            {bulkSummary.failures.length > 0 && (
              <Box component="ul" className="guides-list__failures">
                {bulkSummary.failures.map((f) => (
                  <li key={f._id}>
                    <RouterLink to={`/guides/${f._id}`}>{f.title}</RouterLink>: {f.problem}
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {selectedCount > 0 && (
          <Box className="guides-list__bulkbar" role="region" aria-label="Bulk actions">
            <Typography component="span" className="guides-list__bulkbar-count">
              {selectedCount} selected
            </Typography>
            <Button size="small" variant="contained" disabled={bulkBusy} onClick={() => setBulkConfirm('ACTIVE')}>
              Publish selected
            </Button>
            <Button size="small" variant="outlined" disabled={bulkBusy} onClick={() => setBulkConfirm('DRAFT')}>
              Unpublish selected
            </Button>
            <Button size="small" variant="text" disabled={bulkBusy} onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </Box>
        )}

        {loading ? (
          <SkeletonLoader rows={12} columns={columns.length} />
        ) : loadError ? (
          <Alert severity="error" action={<Button size="small" onClick={reload}>Try again</Button>}>
            {loadError}
          </Alert>
        ) : rows.length === 0 ? (
          <Box className="guides-list__empty">
            <Typography color="text.secondary" variant="body2">
              {query.statuses.length || query.pageType || query.q
                ? 'No guides match these filters.'
                : 'No guides yet. Create one from a document.'}
            </Typography>
          </Box>
        ) : (
          <MintlyTable
            pagination={{
              enabled: total > GUIDE_PAGE_SIZE,
              page: query.page,
              rowsPerPage: GUIDE_PAGE_SIZE,
              totalNumberOfRows: total,
              handleChangePage: (_e, page) => setQuery({ ...query, page }),
            }}
            rows={rows.map((r) => ({ ...r, id: r._id }))}
            selectors={columns}
            onRowClick={(row: GuideRow) => navigate(`/guides/${row._id}`)}
            tableHeadRow={{
              title: (
                <Box className="guides-list__title-head">
                  <Checkbox
                    size="small"
                    className="guides-list__select-all"
                    checked={allOnPage}
                    indeterminate={!allOnPage && someOnPage}
                    onChange={toggleAll}
                    slotProps={{ input: { 'aria-label': 'Select every guide on this page' } }}
                  />
                  Title
                </Box>
              ),
              pageType: <>Type</>,
              status: <>Status</>,
              warnings: <>Review</>,
              datePublished: <>Published</>,
              viewCount: <>Views</>,
              actions: <>Actions</>,
            }}
            tableBodyRow={{
              title: (row: GuideRow) => (
                <Box className="guides-list__title">
                  {/* Its own click: the row opens the editor. */}
                  <Box className="guides-list__select" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selected.has(row._id)}
                      onChange={() => toggleOne(row._id)}
                      slotProps={{ input: { 'aria-label': `Select ${row.title}` } }}
                    />
                  </Box>
                  <Box className="guides-list__title-text">
                    <strong>{row.title}</strong>
                    <Typography variant="caption" color="text.secondary" component="div">/{row.slug}</Typography>
                  </Box>
                </Box>
              ),
              pageType: (row: GuideRow) => <>{TYPE_LABEL[row.pageType] || row.pageType}</>,
              status: (row: GuideRow) => publishToggle(row),
              warnings: (row: GuideRow) =>
                isImportRow(row) ? <>-</> : <DraftWarningsCell state={draftWarnings[row._id]} />,
              datePublished: (row: GuideRow) => <>{formatDate(row.datePublished)}</>,
              viewCount: (row: GuideRow) => <>{row.viewCount ?? 0}</>,
              actions: (row: GuideRow) => (
                <Box className="guides-list__actions" onClick={(e) => e.stopPropagation()}>
                  {isImportRow(row) && (
                    retrying[row._id] !== undefined ? (
                      <Tooltip title={`Retrying, ${retrying[row._id]}s`}>
                        <Box className="guides-list__retrying" aria-label="Retrying import">
                          <CircularProgress size={16} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title={row.sourceFileUrl ? 'Retry import' : 'No archived source to retry from'}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Retry import"
                            disabled={!row.sourceFileUrl}
                            onClick={() => onRetry(row)}
                          >
                            <ReplayOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  )}
                  <Tooltip title="Edit"><IconButton size="small" aria-label="Edit" onClick={() => navigate(`/guides/${row._id}`)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Preview"><IconButton size="small" aria-label="Preview" onClick={() => onPreview(row)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  {row.sourceFileUrl && (
                    <Tooltip title="Download source"><IconButton size="small" aria-label="Download source" onClick={() => onDownloadSource(row)}><FileDownloadOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" color="error" onClick={() => setPendingDelete(row)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              ),
            }}
          />
        )}
      </Box>

      <ConfirmDialog
        open={bulkConfirm !== null}
        title={bulkConfirm === 'ACTIVE' ? `Publish ${selectedCount} guide(s)?` : `Unpublish ${selectedCount} guide(s)?`}
        body={
          bulkConfirm === 'ACTIVE' ? (
            <>
              Each guide is checked against the same rules as a single publish. The ones that pass go
              live at their URLs now; the rest stay as they are and are listed with what to fix.
            </>
          ) : (
            <>
              Published guides among these go back to draft, and their URLs start returning 404 until
              they are published again. Guides that are not live are left alone.
            </>
          )
        }
        actionLabel={bulkConfirm === 'ACTIVE' ? 'Publish' : 'Unpublish'}
        actionColor={bulkConfirm === 'ACTIVE' ? 'primary' : 'error'}
        busy={bulkBusy}
        onConfirm={onBulk}
        onCancel={() => setBulkConfirm(null)}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this guide?"
        body={
          <>
            <strong>{pendingDelete?.title}</strong> is removed from the site. If it is published,
            its URL starts returning 404. Archiving returns 404 too, but keeps the record.
          </>
        }
        busy={deleting}
        onConfirm={onDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </CRMPageTemplate>
  );
};

export default Guides;
